import { parse } from 'querystring';
import { NotificationModel } from '../../components/AccountNotifications';
import { ActionType, IntegrationStatus, IntegrationType } from '../../../../lib/api/account-linking/types';
import { CreateUniqueId } from './Identifiers';
import { TFunction } from 'i18next';

interface AccountLinkingResponse {
  actionableAction: boolean;
  integrationType?: IntegrationType;
  slug?: string;
  success?: boolean;
  accountStatus?: IntegrationStatus;
  action?: ActionType;
  accountId?: string;
  errorCode?: number;
}

// Error codes the linking edge returns for failures a customer can actually act on, keyed by
// integration slug and then by code. The slug matters because the edge does not allocate codes
// from one shared range: Shein declare theirs locally, and Temu rethrow whatever their own API
// returned (TemuApi.ThrowErrors), so a number only has meaning alongside the integration that
// produced it. Anything not listed here falls back to the generic per-action message.
const ERROR_CODE_MESSAGES: Record<string, Record<number, { key: string; fallback: string }>> = {
  shein: {
    4001: {
      key: 'ACCOUNT_LINK_FAIL_UNSUPPORTED_STORE_TYPE',
      fallback:
        "We can't link this store. We support self-operated and semi-managed SHEIN stores; fully-managed, SHEIN-operated and POP stores aren't supported."
    }
  },
  // Ecwid has two linking paths and a tenant setting picks which one its sellers get, so both sets
  // of copy have to exist at once. 410x is the App Market install, available only to tenants with a
  // published listing: those failures arrive after a redirect back, with no field on screen to
  // correct, so the message names the next thing to do. 411x is the custom-app paste, which every
  // tenant can use: there the seller is still looking at the box that is wrong and the message says
  // which. Eight codes rather than one because a single "we were unable to link your account" is
  // what turns a retry into a support ticket.
  ecwid: {
    4101: {
      key: 'ACCOUNT_LINK_FAIL_ECWID_STORE_NOT_FOUND',
      fallback:
        "We couldn't reach the Ecwid store you connected. Try installing our app again from the Ecwid App Market."
    },
    4102: {
      key: 'ACCOUNT_LINK_FAIL_ECWID_INVALID_TOKEN',
      fallback:
        "Ecwid didn't complete the connection. This usually means the install link had already been used or had expired - start the install again from Ecwid and we'll bring you straight back."
    },
    4103: {
      key: 'ACCOUNT_LINK_FAIL_ECWID_INSUFFICIENT_SCOPE',
      fallback:
        "Our app doesn't have all the permissions it needs on your Ecwid store. In your Ecwid control panel, open Apps, remove it, then install it again and approve the permissions when Ecwid asks."
    },
    4104: {
      key: 'ACCOUNT_LINK_FAIL_ECWID_STORE_UNAVAILABLE',
      fallback:
        "Your store isn't available at the moment - it may be paused for maintenance, or on a plan that doesn't include API access. Try again once it's back."
    },

    // 411x are the same four failures reached the other way: the seller created their own app and
    // pasted its credentials, which is how tenants without a published app market listing link.
    // They need their own codes because the remedy differs - this seller is still looking at the
    // field that is wrong, so the copy names it instead of telling them to install again.
    4111: {
      key: 'ACCOUNT_LINK_FAIL_ECWID_CUSTOM_APP_STORE_NOT_FOUND',
      fallback:
        "We couldn't find a store with that store ID. It's the number shown in the footer of any page in your store's control panel."
    },
    4112: {
      key: 'ACCOUNT_LINK_FAIL_ECWID_CUSTOM_APP_INVALID_TOKEN',
      fallback: `That token wasn't accepted. Check you copied the secret token - the one starting "secret_" - from your app's Details page, and that it belongs to the same store as the store ID.`
    },
    4113: {
      key: 'ACCOUNT_LINK_FAIL_ECWID_CUSTOM_APP_INSUFFICIENT_SCOPE',
      fallback:
        "Your app doesn't have the permissions we need. Open your app's Details page and add these access scopes: Read orders, Update orders, and Read store profile. Then uninstall and reinstall the app and copy the new secret token."
    },
    4114: {
      key: 'ACCOUNT_LINK_FAIL_ECWID_CUSTOM_APP_STORE_UNAVAILABLE',
      fallback:
        "Your store isn't available at the moment - it may be paused for maintenance, or on a plan that doesn't include API access. Try again once it's back."
    }
  }
};

export function AccountLinkingMessageHelper(
  action: ActionType,
  success: boolean,
  t: TFunction<string, undefined>,
  errorCode?: number,
  slug?: string
) {
  if (!success && errorCode !== undefined && slug) {
    // The edge echoes back the slug it was called with, so match it without regard to case.
    const specificMessage = ERROR_CODE_MESSAGES[slug.toLowerCase()]?.[errorCode];
    if (specificMessage) {
      return t(specificMessage.key, { defaultValue: specificMessage.fallback });
    }
  }

  switch (action) {
    case 'Configure':
      return success
        ? t(`ACCOUNT_${action.toUpperCase()}_SUCCESS`, { defaultValue: `Account updated` })
        : t(`ACCOUNT_${action.toUpperCase()}_FAIL`, { defaultValue: `We were unable to update your account` });

    case 'Link':
    case 'Install':
      return success
        ? t(`ACCOUNT_${action.toUpperCase()}_SUCCESS`, { defaultValue: `You have successfully linked your account` })
        : t(`ACCOUNT_${action.toUpperCase()}_FAIL`, { defaultValue: `We were unable to link your account` });

    case 'Delete':
      return success
        ? t(`ACCOUNT_${action.toUpperCase()}_SUCCESS`, { defaultValue: `Account deleted` })
        : t(`ACCOUNT_${action.toUpperCase()}_FAIL`, { defaultValue: `We were unable to delete your account` });

    case 'Renew':
      return success
        ? t(`ACCOUNT_${action.toUpperCase()}_SUCCESS`, { defaultValue: `Account successfully renewed` })
        : t(`ACCOUNT_${action.toUpperCase()}_FAIL`, { defaultValue: `We were unable to renew your account` });

    default:
      return success
        ? t(`ACCOUNT_DEFAULT_ACTION_SUCCESS`, { defaultValue: `Success` })
        : t(`ACCOUNT_DEFAULT_ACTION_FAIL`, { defaultValue: `Failed` });
  }
}

export function AccountLinkingResponseHelper(url: string): AccountLinkingResponse {
  //?IntegrationSlug=ebay&IntegrationType=Marketplace&Success=True&Action=Link&AccountId=EB61552&AccountStatus=Active
  //?IntegrationSlug=temu&IntegrationType=Marketplace&Success=True&Action=Install&AccountStatus=Active

  const u = (url || window.location.href).split('?')[1];
  if (!u || u.indexOf('Action=') == -1) {
    return { actionableAction: false };
  }

  const parsed = parse(u);

  const rawErrorCode = parsed.ErrorCode as string;
  const parsedErrorCode = rawErrorCode ? Number(rawErrorCode) : NaN;

  const accountLinkingResponse = {
    actionableAction: true,
    accountId: parsed.AccountId as string,
    accountStatus: parsed.AccountStatus as IntegrationStatus,
    action: parsed.Action as ActionType,
    integrationType: parsed.IntegrationType as IntegrationType,
    slug: parsed.IntegrationSlug as string,
    success: ((parsed.Success as string) || '').toLowerCase() === 'true',
    errorCode: Number.isNaN(parsedErrorCode) ? undefined : parsedErrorCode
  };
  return accountLinkingResponse;
}

export function AccountActionResponseHelper(
  action: ActionType,
  integrationType: IntegrationType,
  integrationSlug: string,
  success: boolean,
  accountId: string,
  configRequired: boolean,
  t: TFunction<string, undefined>
): NotificationModel {
  return {
    color: success ? 'success' : 'error',
    note: AccountLinkingMessageHelper(action, success, t),
    id: CreateUniqueId(),
    integrationSlug: integrationSlug,
    integrationType: integrationType,
    accountId: accountId,
    actions: [],
    showClose: true,
    configRequired: configRequired
  };
}
